/**
 * client/src/lib/listenPrefs.ts — the listener's own settings: speed, and what the car's buttons do.
 *
 * Per-device and per-browser on purpose, like `continuous.ts`: a speed is a habit of the person on
 * this phone, and the steering wheel belongs to this phone's car. Nothing here goes to the server.
 *
 * Every read and write is wrapped. Private browsing, a full quota or a blocked origin each throw from
 * `localStorage`, and a preference that throws into `useNarration` is a book that will not open.
 * `store` is injectable so `test/listen-prefs.test.ts` needs no DOM.
 */

/** §5.7's range, widened at the top for people who listen at 2.5×, with the fine steps around 1×. */
export const PLAYBACK_RATES = [0.75, 1, 1.1, 1.2, 1.3, 1.5, 1.75, 2, 2.5] as const;

export type TrackButtons = "skip" | "chapter";

/** How far a `nexttrack`/`previoustrack` press moves when the setting is `skip`. */
export const TRACK_SKIP_SEC = 30;

export interface PrefStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const RATE_DEFAULT_KEY = "ab.rate";
const rateKey = (bookId: string): string => `ab.rate.${bookId}`;
const TRACK_KEY = "ab.trackButtons";

function browserStore(): PrefStore | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

function read(store: PrefStore | null, key: string): string | null {
  if (store === null) return null;
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}

function write(store: PrefStore | null, key: string, value: string): void {
  if (store === null) return;
  try {
    store.setItem(key, value);
  } catch {
    /* quota or private mode: the setting lasts as long as the tab, which is still something */
  }
}

/** A stored rate, or null when absent or not a rate we offer bounds for. */
function parseRate(raw: string | null): number | null {
  if (raw === null) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return n >= PLAYBACK_RATES[0] && n <= PLAYBACK_RATES[PLAYBACK_RATES.length - 1]! ? n : null;
}

/** This book's speed, else the speed last chosen on any book, else 1×. */
export function loadRate(bookId: string, store: PrefStore | null = browserStore()): number {
  return parseRate(read(store, rateKey(bookId))) ?? parseRate(read(store, RATE_DEFAULT_KEY)) ?? 1;
}

/** Remember the speed for this book, and make it the default for books not yet given one. */
export function saveRate(bookId: string, rate: number, store: PrefStore | null = browserStore()): void {
  if (parseRate(String(rate)) === null) return;
  write(store, rateKey(bookId), String(rate));
  write(store, RATE_DEFAULT_KEY, String(rate));
}

/**
 * What the car's ⏭/⏮ (and the headphones' double/triple press) do. Default `skip`.
 *
 * Chapter is what the lock screen convention says, and it is wrong in a car: a steering-wheel
 * button pressed to hear a sentence again jumps up to an hour of audiobook, with no screen to undo it.
 */
export function loadTrackButtons(store: PrefStore | null = browserStore()): TrackButtons {
  return read(store, TRACK_KEY) === "chapter" ? "chapter" : "skip";
}

export function saveTrackButtons(mode: TrackButtons, store: PrefStore | null = browserStore()): void {
  write(store, TRACK_KEY, mode);
}
